import { WORDPRESS_API_URL } from '#/consts/blog'
import { POSTS_PER_PAGE } from './constants'

const API_URL = WORDPRESS_API_URL

interface ApiProps {
  variables: {
    id?: string
    idType?: string
    onlyEnabled?: boolean
    preview?: boolean
    postsPerPage?: number
    after?: string | string[] | null
    first?: number | null
    pageNo?: number
    categoryName?: string | null
  }
}

async function fetchAPI(
  query: string,
  { variables }: ApiProps = {} as ApiProps
) {
  const headers = { 'Content-Type': 'application/json' } as any

  if (process.env.WORDPRESS_AUTH_REFRESH_TOKEN) {
    headers.Authorization = `Bearer ${process.env.WORDPRESS_AUTH_REFRESH_TOKEN}`
  }

  const res = await fetch(API_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query,
      variables
    })
  })

  const json = await res.json()
  if (json.errors) {
    console.error(json.errors)
    throw new Error('Failed to fetch API')
  }
  return json.data
}

export async function getPreviewPost(id: string, idType = 'DATABASE_ID') {
  const data = await fetchAPI(
    `
    query PreviewPost($id: ID!, $idType: PostIdType!) {
      post(id: $id, idType: $idType) {
        databaseId
        slug
        status
      }
    }`,
    { variables: { id, idType } }
  )
  return data.post
}

export async function getAllPostsWithSlug() {
  const data = await fetchAPI(`
    {
      posts(first: 10000) {
        edges {
          node {
            slug
          }
        }
      }
    }
  `)
  return data?.posts
}

export async function getAllPostsForHome(
  preview: boolean,
  pageNo: number,
  first?: number,
  after?: string | string[] | null,
  categoryName?: string | null
) {
  const data = await fetchAPI(
    `query AllPosts($pageNo: Int, $categoryName: String, $postsPerPage: Int) {
      posts(where: { orderby: { field: DATE, order: DESC }, categoryName: $categoryName, offsetPagination: {offset: $pageNo, size: $postsPerPage}}) {
        pageInfo {
          offsetPagination {
            hasMore
            hasPrevious
            total
          }
        }
        edges {
          cursor
          node {
            title
            excerpt
            slug
            date
            featuredImage {
              node {
                sourceUrl
                mediaDetails {
                  height
                  width
                }
              }
            }
            author {
              node {
                name
                firstName
                lastName
                avatar(size: 32) {
                  url
                  height
                  width
                }
              }
            }
            categories {
              edges {
                node {
                  name
                }
              }
            }
          }
        }
      }
    }
  `,
    {
      variables: {
        onlyEnabled: !preview,
        pageNo: (pageNo - 1) * POSTS_PER_PAGE,
        preview,
        postsPerPage: POSTS_PER_PAGE,
        first,
        after,
        categoryName
      }
    }
  )

  return data?.posts
}

export async function getPostAndMorePosts(
  slug: string | string[] | undefined,
  preview: boolean,
  previewData?: { post: any }
) {
  const postPreview = preview && previewData?.post
  // The slug may be the id of an unpublished post
  const isId = Number.isInteger(Number(slug))
  const isSamePost = isId ?
    Number(slug) === postPreview?.id :
    slug === postPreview?.slug
  const isDraft = isSamePost && postPreview?.status === 'draft'
  const isRevision = isSamePost && postPreview?.status === 'publish'
  const data = await fetchAPI(
    `
    fragment SeoFragment on PostTypeSEO {
      breadcrumbs {
        text
        url
      }
      title
      metaDesc
      metaRobotsNoindex
      metaRobotsNofollow
      opengraphAuthor
      opengraphDescription
      opengraphTitle
      metaKeywords
      opengraphType
      opengraphUrl
      schema {
        raw
      }
      twitterDescription
      twitterTitle
      twitterImage {
        altText
        sourceUrl
      }
      opengraphPublishedTime
      opengraphModifiedTime
      opengraphImage {
        sourceUrl
        altText
      }
    }  

    fragment AuthorFields on User {
      name
      firstName
      lastName
      avatar(size: 32) {
        url
        width
        height
      }
    }
    fragment PostFields on Post {
      title
      excerpt
      slug
      date
      featuredImage {
        node {
          sourceUrl
          mediaDetails {
            height
            width
          }
        }
      }
      author {
        node {
          ...AuthorFields
        }
      }
      categories {
        edges {
          node {
            name
          }
        }
      }
      tags {
        edges {
          node {
            name
          }
        }
      }
      seo { 
        ...SeoFragment
      }
    }
    query PostBySlug($id: ID!, $idType: PostIdType!) {
      post(id: $id, idType: $idType) {
        ...PostFields
        content
        ${
  // Only some of the fields of a revision are considered as there are some inconsistencies
  isRevision ?
    `
        revisions(first: 1, where: { orderby: { field: MODIFIED, order: DESC } }) {
          edges {
            node {
              title
              excerpt
              content
              author {
                node {
                  ...AuthorFields
                }
              }
            }
          }
        }
        ` :
    ''
}
      }
      posts(first: 3, where: { orderby: { field: DATE, order: DESC } }) {
        edges {
          node {
            ...PostFields
          }
        }
      }
    }
  `,
    {
      variables: {
        id: isDraft ? postPreview.id : slug,
        idType: isDraft ? 'DATABASE_ID' : 'SLUG'
      }
    }
  )

  // Draft posts may not have an slug
  if (isDraft) data.post.slug = postPreview.id
  // Apply a revision (changes in a published post)
  if (isRevision && data.post.revisions) {
    const revision = data.post.revisions.edges[0]?.node

    if (revision) Object.assign(data.post, revision)
    delete data.post.revisions
  }

  return data
}
